import React, { useEffect, useState } from 'react';
import useToken from '../../hooks/useToken';

const ANSWERS_KEY  = cat => `dr_answers_${cat}`;
const COMPLETE_KEY = cat => `dr_complete_${cat}`;

export default function DRQuestionPanel({ category }) {
  const { getValidToken } = useToken();

  const [questions,       setQuestions]       = useState([]);
  const [answers,         setAnswers]         = useState({});
  const [results,         setResults]         = useState(null);
  const [currentIdx,      setCurrentIdx]      = useState(0);
  const [minimized,       setMinimized]       = useState(false);
  const [justCompleted,   setJustCompleted]   = useState(false);
  const [alreadyComplete, setAlreadyComplete] = useState(false);
  const [loading,         setLoading]         = useState(true);
  const [validating,      setValidating]      = useState(false);
  const [countdown,       setCountdown]       = useState(5);

  const showQuiz = new URLSearchParams(window.location.search).get('quiz') === '1';

  useEffect(() => {
    if (!showQuiz) { setLoading(false); return; }
    (async () => {
      try {
        const auth = await getValidToken();

        const qRes  = await fetch('/api/dr/questions', { headers: { Authorization: `Bearer ${auth}` } });
        const qData = await qRes.json();
        const isTm  = category === 'threat_model';
        const filtered = qData.filter(q => q.tm_related === isTm);
        setQuestions(filtered);
        if (filtered.length === 0) { setLoading(false); return; }

        const saved = localStorage.getItem(ANSWERS_KEY(category));
        if (saved) {
          try { setAnswers(JSON.parse(saved)); } catch (_) {}
        }

        const sRes   = await fetch('/api/dr/status', { headers: { Authorization: `Bearer ${auth}` } });
        const status = sRes.ok ? await sRes.json() : null;
        const catKey = category === 'design_doc' ? 'design_doc' : 'threat_model';
        const cat    = status?.[catKey];

        if (cat?.all_correct) {
          setAlreadyComplete(true);
          localStorage.setItem(COMPLETE_KEY(category), 'true');
          const prefilled = {};
          filtered.forEach(q => { prefilled[String(q.id)] = { correct: true }; });
          setResults(prefilled);
        }
      } catch (e) {
        console.error('DRQuestionPanel init error:', e);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(ANSWERS_KEY(category), JSON.stringify(answers));
    }
  }, [answers, category]);

  // Countdown + notify DR tab when just completed
  useEffect(() => {
    if (!justCompleted) return;
    localStorage.setItem(COMPLETE_KEY(category), 'true');
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(id);
          try { window.close(); } catch (_) {}
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [justCompleted, category]);

  const handleValidate = async () => {
    setValidating(true);
    try {
      const auth = await getValidToken();
      const res  = await fetch('/api/dr/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
        body: JSON.stringify({ answers, category }),
      });
      const data = await res.json();
      setResults(data.results);
      if (data.all_correct) setJustCompleted(true);
    } catch (e) {
      console.error('Validate error:', e);
    } finally {
      setValidating(false);
    }
  };

  if (!showQuiz || loading || questions.length === 0) return null;

  const current      = questions[currentIdx];
  if (!current) return null;

  let choices = [];
  try { choices = JSON.parse(current.serialized_choices); } catch (_) {}

  const answeredCount = questions.filter(q => answers[String(q.id)] !== undefined).length;
  const correctCount  = results
    ? Object.values(results).filter(r => r.correct).length
    : null;
  const currentResult = results?.[String(current.id)];

  const headerBg     = alreadyComplete ? '#d1fae5' : '#f8f9fa';
  const borderColor  = alreadyComplete ? '#10b981' : '#dee2e6';

  if (minimized) {
    const statusLabel = alreadyComplete
      ? '✅ Completed'
      : results
        ? `${correctCount}/${questions.length} correct`
        : `${answeredCount}/${questions.length} answered`;
    return (
      <div
        onClick={() => setMinimized(false)}
        style={{
          position: 'fixed', bottom: 20, left: 20, zIndex: 9999,
          background: headerBg, border: `1.5px solid ${borderColor}`,
          borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', cursor: 'pointer',
          fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13,
        }}
      >
        <span style={{ fontWeight: 700 }}>📝 Questions</span>
        <span style={{ color: alreadyComplete ? '#065f46' : '#555' }}>{statusLabel}</span>
        <span style={{ color: '#888', fontSize: 16, marginLeft: 4 }}>▲</span>
      </div>
    );
  }

  return (
    <>
      {justCompleted && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10001, fontFamily: 'Inter, system-ui, sans-serif',
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '40px 48px',
            maxWidth: 440, width: '90%', textAlign: 'center',
            boxShadow: '0 8px 48px rgba(0,0,0,0.22)',
          }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🎉</div>
            <h3 style={{ color: '#065f46', marginBottom: 12, fontSize: 20 }}>All answers correct!</h3>
            <p style={{ color: '#444', lineHeight: 1.6, marginBottom: 8 }}>
              Head back to the <strong>Documentation Review</strong> tab to continue.
            </p>
            <p style={{ color: '#555', lineHeight: 1.6, marginBottom: 24 }}>
              This window closes in{' '}
              <strong style={{ fontSize: 18 }}>{countdown}</strong>{' '}
              second{countdown !== 1 ? 's' : ''}.
            </p>
            <p style={{ color: '#999', fontSize: 12, marginBottom: 24 }}>
              You can access this document again in the experiment step.
            </p>
            <button
              onClick={() => { try { window.close(); } catch (_) {} }}
              style={{
                padding: '10px 28px', background: '#10b981', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer',
                fontSize: 14, fontWeight: 600,
              }}
            >
              Close now
            </button>
          </div>
        </div>
      )}

      <div style={{
        position: 'fixed', bottom: 20, left: 20, zIndex: 9999,
        width: 360, background: '#fff',
        border: `1.5px solid ${borderColor}`,
        borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.16)',
        fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '11px 16px', background: headerBg,
          borderBottom: '1px solid #dee2e6',
          borderRadius: '12px 12px 0 0',
        }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>
            📝 {currentIdx + 1} / {questions.length}
          </span>
          {alreadyComplete && (
            <span style={{ fontSize: 12, color: '#065f46', fontWeight: 600 }}>✅ Completed</span>
          )}
          {results && !alreadyComplete && (
            <span style={{
              fontSize: 12, fontWeight: 600,
              color: correctCount === questions.length ? '#065f46' : '#dc3545',
            }}>
              {correctCount}/{questions.length} correct
            </span>
          )}
          <button
            onClick={() => setMinimized(true)}
            title="Minimize"
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 18, color: '#888',
              padding: '0 2px', lineHeight: 1,
            }}
          >▼</button>
        </div>

        {/* Question + choices */}
        <div style={{ padding: '14px 16px', overflowY: 'auto', maxHeight: 320 }}>
          <p style={{
            fontWeight: 600, lineHeight: 1.5, color: '#1a1a1a',
            marginBottom: 14, fontSize: 13,
          }}>
            {current.title}
          </p>

          {choices.map((choice, idx) => {
            const isSelected = answers[String(current.id)] === idx;
            const isCorrect  = currentResult?.correct  && isSelected;
            const isWrong    = currentResult !== undefined && !currentResult.correct && isSelected;
            return (
              <label
                key={idx}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  marginBottom: 9, cursor: alreadyComplete ? 'default' : 'pointer',
                  padding: '8px 10px', borderRadius: 8, transition: 'all 0.15s',
                  border: `1.5px solid ${
                    isCorrect ? '#10b981' : isWrong ? '#ef4444' : isSelected ? '#6366f1' : '#e5e7eb'
                  }`,
                  background: isCorrect ? '#d1fae5' : isWrong ? '#fee2e2' : isSelected ? '#eef2ff' : '#fff',
                }}
              >
                <input
                  type="radio"
                  name={`q-${current.id}`}
                  checked={isSelected}
                  disabled={alreadyComplete}
                  onChange={() => {
                    setAnswers(prev => ({ ...prev, [String(current.id)]: idx }));
                    setResults(null);
                  }}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, lineHeight: 1.4 }}>{choice}</span>
                {isCorrect && <span style={{ marginLeft: 'auto', color: '#10b981', fontWeight: 700, flexShrink: 0 }}>✓</span>}
                {isWrong   && <span style={{ marginLeft: 'auto', color: '#ef4444', fontWeight: 700, flexShrink: 0 }}>✗</span>}
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 16px', borderTop: '1px solid #dee2e6',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#fafafa', borderRadius: '0 0 12px 12px', gap: 8,
        }}>
          <button
            onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
            disabled={currentIdx === 0}
            style={navBtn(currentIdx === 0)}
          >◀ Prev</button>

          {!alreadyComplete && (
            <button
              onClick={handleValidate}
              disabled={validating || answeredCount === 0}
              style={{
                padding: '7px 18px', fontSize: 13, fontWeight: 600,
                background: '#6366f1', color: '#fff',
                border: 'none', borderRadius: 7,
                cursor: (validating || answeredCount === 0) ? 'default' : 'pointer',
                opacity: (validating || answeredCount === 0) ? 0.55 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {validating ? '…' : 'Validate'}
            </button>
          )}

          <button
            onClick={() => setCurrentIdx(i => Math.min(questions.length - 1, i + 1))}
            disabled={currentIdx === questions.length - 1}
            style={navBtn(currentIdx === questions.length - 1)}
          >Next ▶</button>
        </div>
      </div>
    </>
  );
}

const navBtn = disabled => ({
  padding: '7px 14px', fontSize: 13,
  cursor: disabled ? 'default' : 'pointer',
  border: '1px solid #dee2e6', borderRadius: 7, background: '#fff',
  opacity: disabled ? 0.4 : 1, transition: 'opacity 0.2s',
});
